import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { humanizeCount } from 'helpers/humanizeCount';
import { Area, CartesianGrid, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { curveBumpX } from 'd3-shape';
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';
import { Text } from 'rebass/styled-components';
import CustomTooltip from './customTooltip';

const LINE_KEY = 'Number of Employees';
const BUMP_INT = curveBumpX;
const CHART_WIDTH = 450;

const CompanySizeGraph = ({ organizationSizes, workExpereinceInCompany, profileName }) => {
  const { colors } = useTheme();

  const HEADCOUNT_CHART_DEFAULT_LEGEND = [
    {
      id: 'square',
      type: 'square',
      value: profileName,
      color: '#75b5ff',
    },
    {
      id: 'line',
      type: 'line',
      value: LINE_KEY,
      color: colors.blue[600],
    },
  ];

  const years = useMemo(
    () =>
      workExpereinceInCompany.reduce((acc, duration) => {
        const start = dayjs(duration.dateFrom);
        const end = duration.dateTo === null ? dayjs(new Date()) : dayjs(duration.dateTo);

        const startYear = start.year();
        const endYear = end?.year() || startYear;

        if (!endYear && !startYear) {
          return acc;
        }
        const tenure = endYear - startYear;

        return [
          ...acc,
          startYear || endYear,
          ...[...Array(tenure > 0 ? tenure : 1)].map((_val, index) => startYear + index + 1),
          endYear,
        ];
      }, []),
    [workExpereinceInCompany],
  );
  const data = organizationSizes.map((size) => ({
    name: dayjs(size[0]).format('YYYY'),
    bar: years.includes(dayjs(size[0]).year()) ? size[1] : null,
    line: size[1],
  }));

  const isDataEmpty = data.every((item) => !item.bar && !item.line);

  return (
    <>
      {!isDataEmpty && (
        <div style={{ marginBottom: '16px' }}>
          <Text
            fontSize="12px"
            fontWeight="medium"
            marginBottom="8px"
            sx={{ color: 'text.subtle', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            COMPANY SIZE
          </Text>
          <ComposedChart
            width={CHART_WIDTH}
            height={225}
            data={data}
            margin={{ top: 10, right: 15, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="#f5f5f5" />
            <XAxis
              interval="preserveStartEnd"
              // minTickGap={1}
              tickLine={false}
              tick={{ fontSize: 10 }}
              dataKey="name"
            />
            <YAxis
              tickLine={false}
              tick={{ fontSize: 10 }}
              tickFormatter={
                // number | string is okay
                humanizeCount
              }
            />
            {/* @ts-ignore */}
            <Tooltip content={<CustomTooltip />} />
            <defs>
              <linearGradient id="colorTenure" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(117, 181, 255)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="rgb(117, 181, 255)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <Area type={BUMP_INT} dataKey="bar" stroke="transparent" fillOpacity={1} fill="url(#colorTenure)" />
            <Line type={BUMP_INT} dot={false} strokeWidth="2" dataKey="line" stroke={colors.blue[600]} connectNulls />
            <Legend
              align="center"
              verticalAlign="bottom"
              payload={HEADCOUNT_CHART_DEFAULT_LEGEND}
              formatter={(value) => (
                <Text style={{ display: 'inline-block' }} fontSize="14px" fontWeight="medium" color="text.main">
                  {value}
                </Text>
              )}
            />
          </ComposedChart>
        </div>
      )}
    </>
  );
};

CompanySizeGraph.propTypes = {
  organizationSizes: PropTypes.array.isRequired,
  workExpereinceInCompany: PropTypes.array.isRequired,
  profileName: PropTypes.string.isRequired,
};

export default CompanySizeGraph;
